import {
  CompanyData,
  FirstSettingProfile,
  LoginUserProfile,
  Needs,
  Responsibilities,
  Solutions,
} from 'src/models/User'
import { mutate } from 'swr'
import { ApiClient } from '../client'
import {
  GET_CURRENT_COMPANY,
  POST_CHATWORK_NOTICE,
  POST_COMPANY_BUY_NEEDS,
  POST_COMPANY_SOLUTIONS,
  POST_EMAIL_NOTICE_FLAG,
  POST_EMAIL_SETTINGS,
  POST_MESSAGE_AVAILABLE,
  POST_SHOW_FACEBOOK_RELATION,
  POST_SLACK_NOTICE,
  POST_SMS_NOTICE_FLAG,
  POST_UPDATE_SHOW_MESSAGE_COUNT_SIDE_MESSAGE,
  PUT_COMPANY,
  PUT_IS_AGREED_POLICY,
  PUT_PROFILE,
  PUT_SCHEDULE_URL,
  PUT_SCHEDULE_URL_BANNER_LAST_CLOSED_AT,
  PUT_SETTLER,
  putFirstSetting,
} from '../endpoints'
import {
  DecodedEmailSettings,
  NeedsUpdateRequest,
  SolutionsUpdateRequest,
} from '../types/currentCompany'
import {
  encodeEmailSettings,
  toCompanyFirstSetting,
  toCompanyUpdateRequest,
  toProfileFormData,
  toSettlerUpdateRequest,
} from './converters'

export const invalidateUser = (shouldNotFetch?: boolean) =>
  shouldNotFetch
    ? mutate(GET_CURRENT_COMPANY, undefined, false)
    : mutate(GET_CURRENT_COMPANY)

export const updateProfile = async (user: LoginUserProfile) => {
  const formdata = await toProfileFormData(user)
  const res = await ApiClient.put(PUT_PROFILE, formdata, {
    headers: {
      'content-type': 'multipart/form-data',
    },
  })
  if (res.error) return res
  invalidateUser()
  return res
}

export const updateCompanyData = async (company: CompanyData) => {
  const companyData = toCompanyUpdateRequest(company)
  const body = { company: companyData }
  const res = await ApiClient.put(PUT_COMPANY, body)
  if (res.error) return res
  invalidateUser()
  return res
}

export const updateScheduleUrl = async (url: string) => {
  const body = { company: { schedule_url: url } }
  const res = await ApiClient.put(PUT_SCHEDULE_URL, body)
  if (res.error) return res
  return res
}

export const updateScheduleUrlBannerLastClosedAt = async () => {
  const res = await ApiClient.put(PUT_SCHEDULE_URL_BANNER_LAST_CLOSED_AT)
  if (res.error) return res
  await invalidateUser()
  return res
}

export const updateIsAgreedpolicy = async () => {
  const res = await ApiClient.put(PUT_IS_AGREED_POLICY)
  if (res.error) return res
  invalidateUser()
  return res
}

export const updateResponsibilities = async (
  responsibilities: Responsibilities,
) => {
  const body = toSettlerUpdateRequest(responsibilities)
  const res = await ApiClient.put(PUT_SETTLER, body)
  if (res.error) return res
  invalidateUser()
  return res
}

export const updateNeeds = async ({ items }: Needs) => {
  const body: NeedsUpdateRequest = {
    company_buy_need: {
      company_buy_need_option_id: items.map(({ value }) => value),
    },
  }
  const res = await ApiClient.post(POST_COMPANY_BUY_NEEDS, body)
  if (res.error) return res
  return res
}

export const updateSolutions = async ({ items }: Solutions) => {
  const body: SolutionsUpdateRequest = {
    company_solution: {
      ids: items.map(({ value }) => value),
    },
  }
  const res = await ApiClient.patch(POST_COMPANY_SOLUTIONS, body)
  if (res.error) return res
  return res
}

export const toggleShowFacebookLinkingPopup = (show: boolean) => {
  const formData = new FormData()
  formData.append('flag', String(show))
  return ApiClient.post(POST_SHOW_FACEBOOK_RELATION, formData, {
    headers: {
      'content-type': 'multipart/form-data',
    },
  })
}

export const updateMessageAvailable = (id: number, flag: boolean) => {
  const formData = new FormData()
  formData.append('id', `${id}`)
  formData.append('data', flag ? '1' : '0')
  return ApiClient.post(POST_MESSAGE_AVAILABLE, formData)
}

export const updateEmailSettings = (id: number, data: DecodedEmailSettings) => {
  const json = encodeEmailSettings(data)
  const formData = new FormData()
  formData.append('id', `${id}`)
  formData.append('data', json)
  return ApiClient.post(POST_EMAIL_SETTINGS, formData)
}

export const updateSmsNoticeFlag = (flag: boolean) => {
  const formData = new FormData()
  formData.append('flag', flag.toString())
  return ApiClient.post(POST_SMS_NOTICE_FLAG, formData)
}

export const updateEmailNoticeFlag = (flag: boolean) => {
  const formData = new FormData()
  formData.append('flag', flag.toString())
  return ApiClient.post(POST_EMAIL_NOTICE_FLAG, formData)
}

export const updateChatworkNotice = async (
  apiToken: string,
  roomId: string,
) => {
  const body = {
    company: { chatwork_api_token: apiToken, chatwork_room_id: roomId },
  }
  return ApiClient.post(POST_CHATWORK_NOTICE, body)
}

export const updateSlackNotice = async (apiToken: string) => {
  const body = {
    company: { slack_api_token: apiToken },
  }
  return ApiClient.post(POST_SLACK_NOTICE, body)
}

export const updateShowMessageCountSideMessage = () => {
  return ApiClient.post(POST_UPDATE_SHOW_MESSAGE_COUNT_SIDE_MESSAGE)
}

export const firstSetting = async (
  company: FirstSettingProfile,
  needsId: string[],
  solutionsId: string[],
) => {
  const body = await toCompanyFirstSetting(company, needsId, solutionsId)
  const res = await ApiClient.put(putFirstSetting, body, {
    headers: {
      'content-type': 'multipart/form-data',
    },
  })
  if (res.error) return res
  invalidateUser()
  return res
}
